import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_VERSION } from '../data/changelog';
import { isNativeShell } from '../lib/nativeBridge';
import {
  BANEPAD_HEARTBEAT_MS,
  BANEPAD_PAD_OFFLINE_AFTER_MS,
  writePadHeartbeat,
} from '../lib/banepadHeartbeat';
import type { BanepadDeviceRegistration } from '../engine/banepadDevice';

// ─────────────────────────────────────────────────────────────────────
// useBanepadHeartbeat — the pad's side of the keepalive.
//
// Beats every BANEPAD_HEARTBEAT_MS (and immediately when the browser reports
// the network is back). A beat that cannot be written counts as a miss; once
// misses have gone on for BANEPAD_PAD_OFFLINE_AFTER_MS — or the browser says
// it is offline — `offline` flips so the pad can show its support screen. The
// pad keeps beating while offline, so it recovers on its own when the network
// returns; nothing is ever un-paired here.
// ─────────────────────────────────────────────────────────────────────
export interface PadHeartbeatState {
  /** True once beats have failed long enough (or navigator says offline). */
  offline: boolean;
  /** Epoch ms of the last successful beat (null before the first). */
  lastOkAt: number | null;
  /** Epoch ms when the current outage started (null when online). */
  offlineSince: number | null;
  /** Force a beat now (the support screen's «Prøv igjen»). */
  retry: () => void;
}

export function useBanepadHeartbeat(
  registration: BanepadDeviceRegistration | null | undefined,
  opts: { intervalMs?: number; offlineAfterMs?: number } = {},
): PadHeartbeatState {
  const intervalMs = opts.intervalMs ?? BANEPAD_HEARTBEAT_MS;
  const offlineAfterMs = opts.offlineAfterMs ?? BANEPAD_PAD_OFFLINE_AFTER_MS;
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [firstFailAt, setFirstFailAt] = useState<number | null>(null);
  const [navOffline, setNavOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  const [tick, setTick] = useState(0);
  const inFlight = useRef(false);

  const beat = useCallback(async () => {
    if (!registration || inFlight.current) return;
    inFlight.current = true;
    const now = Date.now();
    try {
      await writePadHeartbeat({
        deviceId: registration.id,
        courtId: registration.courtId,
        courtName: registration.courtName,
        at: now,
        shell: isNativeShell() ? 'app' : 'browser',
        appVersion: APP_VERSION,
      });
      setLastOkAt(now);
      setFirstFailAt(null);
    } catch {
      setFirstFailAt((t) => t ?? now);
    } finally {
      inFlight.current = false;
    }
  }, [registration]);

  // Beat on mount + interval, and re-evaluate «offline for how long» each tick.
  useEffect(() => {
    if (!registration) return undefined;
    void beat();
    const id = setInterval(() => {
      void beat();
      setTick((t) => t + 1);
    }, intervalMs);
    return () => clearInterval(id);
  }, [registration, beat, intervalMs]);

  // The browser's own view of the network: instant feedback both ways.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const on = () => {
      setNavOffline(false);
      void beat();
    };
    const off = () => setNavOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [beat]);

  void tick; // re-render driver for the elapsed-time check below
  const failedFor = firstFailAt != null ? Date.now() - firstFailAt : 0;
  const offline = navOffline || failedFor > offlineAfterMs;
  const offlineSince = offline ? (firstFailAt ?? lastOkAt ?? Date.now()) : null;

  return { offline, lastOkAt, offlineSince, retry: () => void beat() };
}
